/**
 * Where the app's numbers come from, for the reader (docs/DATA_AND_ML.md §2 is
 * the internal version). Each entry was checked against the publisher's record.
 *
 * Bar for this list: peer-reviewed or official guidance, a population like our
 * users, and no commercial stake in the answer. Left out on 2026-10-06 although
 * DATA_AND_ML §2 still notes them: Oakley 2024 (authors run a chiropractic
 * device business; chronic-pain sample), Aafreen 2023 (neck-pain sample, app
 * checked only against its own desktop software), Karbalaeimahdi 2025 (the
 * head-angle figure pools two papers, n=86).
 *
 * Copy rules (LEGAL §4, `findBannedPhrases`): say what we take from each source,
 * never what it claims about health. Paper titles are left out — several use
 * clinical or "study" wording that our own strings must not carry.
 */
export type SourceTopic = 'tracking' | 'head' | 'seated' | 'standing' | 'breaks';

export interface Source {
  id: string;
  topic: SourceTopic;
  /** Authors, venue, year — enough to find it. */
  cite: string;
  /** What Erectus takes from it, in plain words. */
  use: string;
  url: string;
}

export const SOURCE_TOPICS: Record<SourceTopic, string> = {
  tracking: 'Finding your joints',
  head: 'The head angle',
  seated: 'Sitting position',
  standing: 'Standing check',
  breaks: 'Rest breaks',
};

export const SOURCES: readonly Source[] = [
  {
    id: 'blazepose',
    topic: 'tracking',
    cite: 'Bazarevsky V, Grishchenko I, Raveendran K, et al. BlazePose. CVPR Workshop on Computer Vision for AR/VR, 2020.',
    use: 'The pose model (MediaPipe) that finds 33 body points on your phone. The picture never leaves the device.',
    url: 'https://arxiv.org/abs/2006.10204',
  },
  {
    id: 'one-euro',
    topic: 'tracking',
    cite: 'Casiez G, Roussel N, Vogel D. The 1€ filter. CHI 2012.',
    use: 'Steadies the points between frames, so small wobbles in tracking do not read as movement.',
    url: 'https://doi.org/10.1145/2207676.2208639',
  },
  {
    id: 'wade-2023',
    topic: 'tracking',
    cite: 'Wade L, et al. PLoS One. 2023;18(11):e0293917.',
    use: 'With one camera, joints on the far side of the body carry about twice the error. Erectus measures only the side facing the camera.',
    url: 'https://doi.org/10.1371/journal.pone.0293917',
  },
  {
    id: 'winter-2009',
    topic: 'standing',
    cite: 'Winter DA. Biomechanics and Motor Control of Human Movement. 4th ed. Wiley; 2009.',
    use: 'Body segment proportions (ear to ankle ≈ 0.90 of height), used only to turn standing offsets into centimetres when you give your height.',
    url: 'https://doi.org/10.1002/9780470549148',
  },
  {
    id: 'zarate-tejero-2024',
    topic: 'head',
    cite: 'Zárate-Tejero C, et al. Appl Sci. 2024;14(19):8639.',
    use: 'Across 122 adults the average head angle was 48.8°, about 1.6° lower per decade of age. So we grade the angle and compare you with your own baseline, not with one fixed line.',
    url: 'https://doi.org/10.3390/app14198639',
  },
  {
    id: 'ruivo-2014',
    topic: 'head',
    cite: 'Ruivo RM, Pezarat-Correia P, Carita AI. Braz J Phys Ther. 2014;18(4):364-71.',
    use: 'How the neck angle (base of the neck to the ear) and head tilt (ear to the outer eye corner) are drawn, and the 50° line under which the head counts as forward. In 275 teenagers the neck angle averaged 47.4° and the head tilt 17.2°, so many people sit near the line.',
    url: 'https://doi.org/10.1590/bjpt-rbf.2014.0027',
  },
  {
    id: 'raine-twomey-1997',
    topic: 'head',
    cite: 'Raine S, Twomey LT. Arch Phys Med Rehabil. 1997;78(11):1215-23.',
    use: 'The camera cannot see the bone at the base of the neck (C7). We place it behind the shoulder at the average shoulder-to-C7 angle of 53.7° measured in 160 adults, so the neck angle is an estimate.',
    url: 'https://doi.org/10.1016/S0003-9993(97)90335-X',
  },
  {
    id: 'ansur-2',
    topic: 'head',
    cite: 'Gordon CC, et al. 2012 Anthropometric Survey of U.S. Army Personnel (ANSUR II). Natick, 2014.',
    use: 'Body measurements of about 6,000 adults. From them we take how far the base of the neck sits above the shoulder, relative to torso length, to place C7.',
    url: 'https://www.openlab.psu.edu/ansur2/',
  },
  {
    id: 'gallego-izquierdo-2020',
    topic: 'head',
    cite: 'Gallego-Izquierdo T, et al. Int J Environ Res Public Health. 2020;17(18):6521.',
    use: 'A smartphone measurement of the head angle can differ by about 5° between repeats. That is why a change under 5° shows as “maintained” and the trend line has a ±5° band.',
    url: 'https://doi.org/10.3390/ijerph17186521',
  },
  {
    id: 'osha-workstation',
    topic: 'seated',
    cite: 'OSHA. Computer Workstations eTool: Neutral Working Postures.',
    use: 'Neutral seated positions, upright or reclined, which set the ranges for trunk lean and hip angle.',
    url: 'https://www.osha.gov/etools/computer-workstations/positions',
  },
  {
    id: 'hasegawa-2017',
    topic: 'standing',
    cite: 'Hasegawa K, et al. J Anat. 2017;230(5):619-630.',
    use: 'In 136 healthy adults standing naturally, the ear sat about 5 cm in front of the ankle. The standing check grades you against this kind of measured posture.',
    url: 'https://doi.org/10.1111/joa.12586',
  },
  {
    id: 'woodhull-1985',
    topic: 'standing',
    cite: 'Woodhull AM, Maltrud K, Mello BL. Eur J Appl Physiol. 1985;54(1):109-115.',
    use: 'Side-on photos of people standing: the ear about 6 cm, the shoulder about 4 cm in front of the ankle. The line from ankle to ear leans about 2° forward.',
    url: 'https://doi.org/10.1007/BF00426309',
  },
  {
    id: 'ferreira-2010',
    topic: 'standing',
    cite: 'Ferreira EA, Duarte M, Maldonado EP, et al. Clinics. 2010;65(7):675-681.',
    use: 'The ankle-to-shoulder line measured from side photos: about 1.5–2.7° forward, with readings consistent between people taking them.',
    url: 'https://doi.org/10.1590/S1807-59322010000700005',
  },
  {
    id: 'barra-lopez-2024',
    topic: 'standing',
    cite: 'Barra-López ME. J Rehabil Med. 2024;56:jrm41899.',
    use: 'The textbook “ideal” plumb line does not match how healthy people stand, which is why the check uses measured posture instead.',
    url: 'https://doi.org/10.2340/jrm.v56.41899',
  },
  {
    id: 'dunstan-2012',
    topic: 'breaks',
    cite: 'Dunstan DW, Kingwell BA, Larsen R, et al. Diabetes Care. 2012;35(5):976–983.',
    use: 'Short movement breaks every 20 minutes of sitting — one of the patterns behind breaking up sitting regularly.',
    url: 'https://doi.org/10.2337/dc11-1931',
  },
  {
    id: 'cornell-20-8-2',
    topic: 'breaks',
    cite: 'Hedge A. Cornell University Ergonomics Web: Sit-Stand Working.',
    use: 'The 20-8-2 pattern: 20 minutes sitting, 8 standing, 2 moving, every half hour. The shortest break interval you can choose is 30 minutes.',
    url: 'https://ergo.human.cornell.edu/CUESitStandPrograms.html',
  },
  {
    id: 'exercise-snacks-2026',
    topic: 'breaks',
    cite: 'Zeng et al. BMC Public Health. 2026.',
    use: 'A review of short exercise bouts during long sitting: breaks every 30–50 minutes, 2–10 minutes long, with targeted stretches and neck and shoulder-blade work, kept up for 5–6 weeks or more. The basis for the break reminders, the exercise snacks and the 6-week program.',
    url: 'https://doi.org/10.1186/s12889-026-27292-6',
  },
  {
    id: 'active-break-2024',
    topic: 'breaks',
    cite: 'Labecka MK, et al. BMC Musculoskeletal Disorders. 2024.',
    use: 'Active breaks — moving rather than just pausing — at least every 30 minutes of sitting. Why most breaks in a program carry an exercise snack.',
    url: 'https://doi.org/10.1186/s12891-024-08186-3',
  },
  {
    id: 'postural-shift-2023',
    topic: 'breaks',
    cite: 'Akkarakittichoke N, et al. J Manipulative Physiol Ther. 2023.',
    use: 'How often office workers shift position during an hour of sitting. Why programs ask you to change how you sit between breaks, too.',
    url: 'https://doi.org/10.1016/j.jmpt.2023.06.003',
  },
  {
    id: 'office-msd-2022',
    topic: 'breaks',
    cite: 'Putsa B, et al. BMC Public Health. 2022.',
    use: 'Office workers who stood up or walked at least once an hour. The upper limit for the break interval.',
    url: 'https://doi.org/10.1186/s12889-022-13940-0',
  },
  {
    id: 'waongenngarm-2021',
    topic: 'breaks',
    cite: 'Waongenngarm P, et al. Scand J Work Environ Health. 2021;47(4):306–317.',
    use: 'Rest breaks and position changes through the working day, compared with and without movement in the break. Why programs pair breaks with exercise snacks, and log both.',
    url: 'https://doi.org/10.5271/sjweh.3949',
  },
];
